import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import useOpenFromQuery from '../../../hooks/useOpenFromQuery';
import { withTabInPath } from '../../../utils/navigationState';
import LoadingSpinner from '../../Common/LoadingSpinner';
import TextInput from '../../Common/TextInput';
import CustomSelect from '../../Common/CustomSelect';
import PermissionGuard from '../../Common/PermissionGuard';
import { useAuth } from '../../../contexts/AuthContext';
import inventoryApi from '../../../services/inventoryApi';
import adminApi from '../../../services/adminApi';
import GRNForm from './GRNForm';
import GRNDetails from './GRNDetails';
import InspectionDialog from './InspectionDialog';
import { DoubleEntryModal, DoubleEntryToggleButton } from '../../Finance/DoubleEntries/DoubleEntryPanel';
import { openGRNReport } from '../../../utils/openInventoryReport';
import { canPrintGRN } from '../../../utils/documentPrintGate';

const GRNManagement = () => {
  const { hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [poReturnPath, setPoReturnPath] = useState(null);
  const [doubleEntryGRN, setDoubleEntryGRN] = useState(null); // GRN whose GL double entries are shown
  const [grns, setGRNs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0,
    hasNext: false,
    hasPrev: false
  });

  // View and pagination
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem('grnManagementView') || 'table';
    } catch (error) {
      return 'table';
    }
  });

  // Client-side filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [warehouseFilter, setWarehouseFilter] = useState('all');
  const [companyInfo, setCompanyInfo] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const locationId = localStorage.getItem('X-Location-Id') || localStorage.getItem('lastUsedLocationId');
        if (!locationId) return;
        const res = await adminApi.getLocationById(locationId);
        const loc = res.data?.data || res.data || {};
        const addr = loc.address || {};
        setCompanyInfo({
          name: loc.name || loc.locationName || '',
          street: addr.street || addr.addressLine1 || '',
          city: addr.city || '',
          zipCode: addr.zipCode || addr.postalCode || '',
        });
      } catch (e) {
        console.warn('[GRNManagement] could not load location info:', e.message);
      }
    })();
  }, []);
  const [inspectionFilter, setInspectionFilter] = useState('all');

  // Dialogs and forms
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showInspection, setShowInspection] = useState(false);
  const [editingGRN, setEditingGRN] = useState(null);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showStatusUpdate, setShowStatusUpdate] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusComments, setStatusComments] = useState('');

  // Approval/Rejection workflow
  const [showApproveConfirm, setShowApproveConfirm] = useState(null);
  const [showRejectConfirm, setShowRejectConfirm] = useState(null);
  const [approvalComments, setApprovalComments] = useState('');
  const [rejectionComments, setRejectionComments] = useState('');

  // PO-wise GRN creation
  const [showPOSelection, setShowPOSelection] = useState(false);
  const [availablePOs, setAvailablePOs] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);

  // Bulk operations
  const [selectedGRNs, setSelectedGRNs] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkComments, setBulkComments] = useState('');

  // Reference data
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'pending', label: 'Pending' },
    { value: 'received', label: 'Received' },
    { value: 'inspected', label: 'Inspected' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'partially_accepted', label: 'Partially Accepted' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'returned', label: 'Returned' }
  ];

  const inspectionStatusOptions = [
    { value: 'all', label: 'All Inspection Status' },
    { value: 'not_required', label: 'Not Required' },
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' }
  ];

  useEffect(() => {
    fetchGRNs();
    fetchReferenceData();
  }, [statusFilter, supplierFilter, warehouseFilter, inspectionFilter, pagination.page, pagination.limit]);

  // Handle incoming PO from navigation state (when redirected from PO management)
  useEffect(() => {
    if (location.state?.selectedPO && location.state?.fromPO) {
      // Create a GRN object from the PO data to populate the form
      const poData = location.state.selectedPO;
      
      // Calculate pending quantities for each line item
      const grnLineItems = poData.lineItems?.map(item => {
        const orderedQty = item.quantity || 0;
        const receivedQty = item.receivedQuantity || 0;
        const pendingQty = Math.max(0, orderedQty - receivedQty);
        
        return {
          item: item.item,
          assetId: item.assetId?._id || item.assetId || null,
          serviceId: item.serviceId?._id || item.serviceId || null,
          assetCode: item.assetCode || '',
          assetName: item.assetName || '',
          description: item.description || item.item?.name || item.assetName,
          orderedQuantity: orderedQty,
          receivedQuantity: receivedQty,
          deliveredQuantity: pendingQty,
          acceptedQuantity: pendingQty, // default all delivered as accepted
          rejectedQuantity: 0,
          unitPrice: item.unitPrice || 0,
          condition: 'good',
          purchaseOrderItemId: item._id,
          pendingQuantity: pendingQty
        };
      }) || [];

      const grnFromPO = {
        supplier: poData.supplier,
        selectedSupplier: poData.supplier,
        warehouse: poData.warehouse?._id || poData.warehouse,
        purchaseOrder: poData, // Pass the entire PO object
        referenceNumber: poData.orderNumber,
        lineItems: grnLineItems,
        withPO: true, // Ensure PO mode is enabled
        procurementType: poData.procurementType || 'INVENTORY',
        receiptDate: new Date().toISOString().split('T')[0],
        // a service completion starts from the PO's service date
        ...(poData.procurementType === 'SERVICE' && poData.expectedDeliveryDate && {
          deliveryNote: { number: '', date: poData.expectedDeliveryDate }
        }),
        costCenter: poData.costCenter || '',
        costCenterLabel: poData.costCenterLabel || ''
      };
      
      setEditingGRN(grnFromPO);
      setShowForm(true);
      setPoReturnPath(location.state?.from || '/inventory/purchase-orders');

      // Clear PO payload but keep return navigation context
      navigate(`${location.pathname}${location.search || ''}`, {
        replace: true,
        state: {
          from: location.state?.from,
          fromTab: location.state?.fromTab,
        },
      });
    }
  }, [location.state, location.pathname, location.search, navigate]);

  const fetchGRNs = async () => {
    // Check read permission before making API call
    if (!hasPermission('INVENTORY:GRN:READ')) {
      toast.error('You do not have permission to view GRNs');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        supplier: supplierFilter !== 'all' ? supplierFilter : undefined,
        warehouse: warehouseFilter !== 'all' ? warehouseFilter : undefined,
        inspectionStatus: inspectionFilter !== 'all' ? inspectionFilter : undefined
      };

      const response = await inventoryApi.getGRNs(params);
      setGRNs(response.data || []);

      // Update pagination state with response metadata
      if (response.pagination) {
        setPagination(response.pagination);
      }

    } catch (err) {
      console.error('Error fetching GRNs:', err);
      toast.error(err?.response?.data?.message || 'Failed to fetch GRNs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [suppliersRes, warehousesRes] = await Promise.all([
        inventoryApi.getSuppliers({ limit: 1000 }),
        adminApi.getLocations({ limit: 1000 })
      ]);
      setSuppliers(suppliersRes.data || []);
      setWarehouses(warehousesRes.data?.data || warehousesRes.data || []);
    } catch (err) {
      console.error('Error fetching reference data:', err);
    }
  };

  // Filter GRNs based on search term (client-side filtering for display)
  const filteredGRNs = grns.filter(grn => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      grn.grnNumber?.toLowerCase().includes(searchLower) ||
      grn.deliveryNote?.number?.toLowerCase().includes(searchLower) ||
      grn.invoice?.number?.toLowerCase().includes(searchLower) ||
      grn.supplier?.name?.toLowerCase().includes(searchLower) ||
      grn.warehouse?.name?.toLowerCase().includes(searchLower)
    );
  });

  const handleViewChange = (newView) => {
    setView(newView);
    try {
      localStorage.setItem('grnManagementView', newView);
    } catch (error) {
      console.warn('Failed to save view preference');
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSupplierFilter = (e) => {
    setSupplierFilter(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleWarehouseFilter = (e) => {
    setWarehouseFilter(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleInspectionFilter = (e) => {
    setInspectionFilter(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  // Bulk operations handlers
  const handleSelectGRN = (grnId) => {
    const newSelected = new Set(selectedGRNs);
    if (newSelected.has(grnId)) {
      newSelected.delete(grnId);
    } else {
      newSelected.add(grnId);
    }
    setSelectedGRNs(newSelected);
  };

  const handleSelectAllGRNs = () => {
    if (selectedGRNs.size === filteredGRNs.length) {
      setSelectedGRNs(new Set());
    } else {
      setSelectedGRNs(new Set(filteredGRNs.map(grn => grn._id)));
    }
  };

  const handleBulkAction = (action) => {
    if (selectedGRNs.size === 0) {
      toast.error('Please select at least one GRN');
      return;
    }

    // Check permissions for bulk actions
    if (action === 'approve' && !hasPermission('INVENTORY:GRN:APPROVE')) {
      toast.error('You do not have permission to approve GRNs');
      return;
    }
    if (action === 'reject' && !hasPermission('INVENTORY:GRN:REJECT')) {
      toast.error('You do not have permission to reject GRNs');
      return;
    }
    if (action === 'export' && !hasPermission('INVENTORY:GRN:EXPORT')) {
      toast.error('You do not have permission to export GRNs');
      return;
    }

    setBulkAction(action);
    setBulkComments('');
    setShowBulkActions(true);
  };

  const executeBulkAction = async () => {
    if (!bulkAction) return;

    try {
      setLoading(true);
      const grnIds = Array.from(selectedGRNs);

      switch (bulkAction) {
        case 'approve':
          {
            // One by one: each approval posts its double entry; a refused one does not stop the others
            const results = await Promise.allSettled(grnIds.map(id => inventoryApi.approveGRN(id, bulkComments)));
            const failed = results
              .map((r, i) => ({ r, grn: grns.find(g => g._id === grnIds[i]) }))
              .filter(x => x.r.status === 'rejected')
              .map(x => `${x.grn?.grnNumber || 'GRN'}: ${x.r.reason?.response?.data?.message || x.r.reason?.response?.data?.error || x.r.reason?.message}`);
            const ok = results.length - failed.length;
            if (ok) toast.success(`${ok} GRN(s) approved successfully`);
            if (failed.length) toast.error(`${failed.length} GRN(s) not approved — ${failed.join(' | ')}`);
          }
          break;
        case 'reject':
          if (!bulkComments.trim()) {
            toast.error('Rejection reason is required');
            return;
          }
          await Promise.all(grnIds.map(id => inventoryApi.rejectGRN(id, bulkComments)));
          toast.success(`${grnIds.length} GRN(s) rejected successfully`);
          break;
        case 'updateStatus':
          // Would need a bulk status update endpoint
          toast.error('Bulk status update not yet implemented');
          return;
        case 'export':
          handleBulkExport(grnIds);
          toast.success(`${grnIds.length} GRN(s) exported successfully`);
          break;
        default:
          toast.error('Unknown bulk action');
          return;
      }

      fetchGRNs();
      setSelectedGRNs(new Set());
      setShowBulkActions(false);
      setBulkAction('');
      setBulkComments('');
    } catch (err) {
      console.error('Error executing bulk action:', err);
      toast.error(`Failed to execute bulk action: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkExport = (grnIds) => {
    const selectedGRNData = filteredGRNs.filter(grn => grnIds.includes(grn._id));
    const csvContent = generateCSVContent(selectedGRNData);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `grn_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const generateCSVContent = (grns) => {
    const headers = [
      'GRN Number', 'Date', 'PO Number', 'Supplier', 'Status',
      'Total Delivered Value', 'Total Accepted Value', 'Warehouse'
    ];

    const rows = grns.map(grn => [
      grn.grnNumber,
      formatDate(grn.receiptDate),
      grn.purchaseOrder?.orderNumber || 'N/A',
      grn.supplier?.name || 'N/A',
      grn.status?.replace('_', ' ').toUpperCase(),
      grn.totals?.totalDeliveredValue || 0,
      grn.totals?.totalAcceptedValue || 0,
      grn.warehouse?.name || 'N/A'
    ]);

    return [headers, ...rows].map(row =>
      row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
  };

  const clearError = () => {
  };

  const clearSuccess = () => {
  };

  const handleCreateGRN = () => {
    if (!hasPermission('INVENTORY:GRN:CREATE')) {
      toast.error('You do not have permission to create GRNs');
      return;
    }
    setEditingGRN(null);
    setShowForm(true);
  };

  const handlePOSelect = async (po) => {
    if (!hasPermission('INVENTORY:GRN:CREATE')) {
      toast.error('You do not have permission to create GRNs');
      return;
    }

    try {
      setLoading(true);
      const response = await inventoryApi.createGRNFromPO(po._id);
      toast.success(response?.data?.message || response?.message || 'GRN created successfully from Purchase Order');
      setShowPOSelection(false);
      fetchGRNs();

      // Open the newly created GRN for editing
      if (response.data) {
        setEditingGRN(response.data);
        setShowForm(true);
      }
    } catch (err) {
      console.error('Error creating GRN from PO:', err);
      toast.error(err?.response?.data?.message || 'Failed to create GRN from Purchase Order');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintGRN = async (grn) => {
    await openGRNReport(grn, companyInfo);
  };

  const handleEditGRN = (grn) => {
    if (!hasPermission('INVENTORY:GRN:UPDATE')) {
      toast.error('You do not have permission to edit GRNs');
      return;
    }
    setEditingGRN(grn);
    setShowForm(true);
  };

  const handleViewGRN = async (grn) => {
    if (!hasPermission('INVENTORY:GRN:READ')) {
      toast.error('You do not have permission to view GRN details');
      return;
    }
    setSelectedGRN(grn);
    setShowDetails(true);
    try {
      const res = await inventoryApi.getGRNById(grn._id);
      const full = res?.data?.data || res?.data;
      if (full && full._id) setSelectedGRN(full);
    } catch { /* leave original grn data */ }
  };
  // opened from a link elsewhere (e.g. Vendor History) with ?view=<grn id>
  useOpenFromQuery((id) => handleViewGRN({ _id: id }));

  const handleInspectGRN = (grn) => {
    if (!hasPermission('INVENTORY:GRN:INSPECT')) {
      toast.error('You do not have permission to inspect GRNs');
      return;
    }
    setSelectedGRN(grn);
    setShowInspection(true);
  };

  const handleDeleteGRN = (grn) => {
    if (!hasPermission('INVENTORY:GRN:DELETE')) {
      toast.error('You do not have permission to delete GRNs');
      return;
    }
    setShowDeleteConfirm(grn);
  };

  const handleUpdateStatus = (grn) => {
    if (!hasPermission('INVENTORY:GRN:UPDATE')) {
      toast.error('You do not have permission to update GRN status');
      return;
    }
    setShowStatusUpdate(grn);
    setNewStatus(grn.status);
    setStatusComments('');
  };

  const confirmStatusUpdate = async () => {
    if (!hasPermission('INVENTORY:GRN:UPDATE')) {
      toast.error('You do not have permission to update GRN status');
      return;
    }

    if (!newStatus.trim()) {
      toast.error('Please select a status');
      return;
    }

    try {
      await inventoryApi.updateGRNStatus(showStatusUpdate._id, newStatus, statusComments);
      toast.success(`GRN status updated to ${newStatus.replace('_', ' ')}`);
      fetchGRNs();
      setShowStatusUpdate(null);
      setNewStatus('');
      setStatusComments('');
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error(err?.response?.data?.message || 'Failed to update GRN status. Please try again.');
    }
  };

  const confirmDelete = async () => {
    if (!hasPermission('INVENTORY:GRN:DELETE')) {
      toast.error('You do not have permission to delete GRNs');
      return;
    }

    try {
      await inventoryApi.deleteGRN(showDeleteConfirm._id);
      toast.success('GRN deleted successfully');
      fetchGRNs();
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting GRN:', err);
      toast.error(err?.response?.data?.message || 'Failed to delete GRN. Please try again.');
    }
  };

  const handleApproveGRN = (grn) => {
    if (!hasPermission('INVENTORY:GRN:APPROVE')) {
      toast.error('You do not have permission to approve GRNs');
      return;
    }
    setShowApproveConfirm(grn);
    setApprovalComments('');
  };

  const handleRejectGRN = (grn) => {
    if (!hasPermission('INVENTORY:GRN:REJECT')) {
      toast.error('You do not have permission to reject GRNs');
      return;
    }
    setShowRejectConfirm(grn);
    setRejectionComments('');
  };

  const confirmApproval = async () => {
    if (!hasPermission('INVENTORY:GRN:APPROVE')) {
      toast.error('You do not have permission to approve GRNs');
      return;
    }

    if (!approvalComments.trim()) {
      toast.error('Approval comments are required');
      return;
    }

    try {
      setLoading(true);
      const response = await inventoryApi.approveGRN(showApproveConfirm._id, approvalComments);

      if (response.success) {
        toast.success(response.message || 'GRN approved successfully and PO quantities updated');
        fetchGRNs();
        setShowApproveConfirm(null);
        setShowDetails(false);
        setApprovalComments('');
      } else {
        toast.error(response.message || 'Failed to approve GRN');
      }
    } catch (err) {
      console.error('Error approving GRN:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to approve GRN. Please try again.';
      toast.error(`Approval failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const confirmRejection = async () => {
    if (!hasPermission('INVENTORY:GRN:REJECT')) {
      toast.error('You do not have permission to reject GRNs');
      return;
    }

    if (!rejectionComments.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    try {
      await inventoryApi.rejectGRN(showRejectConfirm._id, rejectionComments);
      toast.success('GRN rejected successfully');
      fetchGRNs();
      setShowRejectConfirm(null);
      setShowDetails(false);
      setRejectionComments('');
    } catch (err) {
      console.error('Error rejecting GRN:', err);
      toast.error(err?.response?.data?.message || 'Failed to reject GRN. Please try again.');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingGRN(null);
    setPoReturnPath(null);
    fetchGRNs();
    toast.success(editingGRN ? 'GRN updated successfully' : 'GRN created successfully');
  };

  const handleInspectionSuccess = () => {
    setShowInspection(false);
    setSelectedGRN(null);
    fetchGRNs();
    toast.success('Inspection completed successfully');
  };

  const getStatusColor = (status) => {
    const statusColors = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-blue-100 text-blue-800',
      received: 'bg-indigo-100 text-indigo-800',
      inspected: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-teal-100 text-teal-800',
      partially_accepted: 'bg-orange-100 text-orange-800',
      rejected: 'bg-red-100 text-red-800',
      returned: 'bg-purple-100 text-purple-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getInspectionStatusColor = (status) => {
    const statusColors = {
      not_required: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-indigo-100 text-indigo-800',
      completed: 'bg-teal-100 text-teal-800',
      failed: 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSupplierFilter('all');
    setWarehouseFilter('all');
    setInspectionFilter('all');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Pagination calculations - now using server-side pagination
  const hasFilters = () => {
    return searchTerm || statusFilter !== 'all' || supplierFilter !== 'all' ||
      warehouseFilter !== 'all' || inspectionFilter !== 'all';
  };

  if (loading && grns.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <PermissionGuard
      permission="INVENTORY:GRN:READ"
      fallback={
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 15v2m0 0v2m0-2h2m-2 0h-2m10-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">
            You do not have permission to access GRN management.
          </p>
        </div>
      }
    >
      <div className="bg-white shadow-sm border border-gray-200">
          {/* Error Display */}
          {error && (
            <div className="mb-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-3">
              <div className="flex items-center">
                <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium text-red-800">{error}</p>
                <button
                  onClick={clearError}
                  className="ml-auto text-red-600 hover:text-red-500"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Success Display */}
          {success && (
            <div className="mb-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded p-3">
              <div className="flex items-center">
                <svg className="h-4 w-4 text-indigo-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium text-indigo-800">{success}</p>
                <button
                  onClick={clearSuccess}
                  className="ml-auto text-indigo-600 hover:text-indigo-500"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Form Modal */}
          {showForm && (
            <GRNForm
              grn={editingGRN}
              onSuccess={handleFormSuccess}
              onCancel={() => {
                setShowForm(false);
                setEditingGRN(null);
                if (poReturnPath) {
                  const fromTab = location.state?.fromTab;
                  navigate(withTabInPath(poReturnPath, fromTab));
                  setPoReturnPath(null);
                }
              }}
            />
          )}

          {/* Details Modal */}
          {showDetails && (
            <GRNDetails
              grn={selectedGRN}
              warehouses={warehouses}
              onClose={() => {
                setShowDetails(false);
                setSelectedGRN(null);
              }}
              onEdit={handleEditGRN}
              onApprove={handleApproveGRN}
              onReject={handleRejectGRN}
              onInspect={handleInspectGRN}
            />
          )}

          {/* Inspection Modal */}
          {showInspection && (
            <InspectionDialog
              grn={selectedGRN}
              onSuccess={handleInspectionSuccess}
              onCancel={() => {
                setShowInspection(false);
                setSelectedGRN(null);
              }}
            />
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2">
              <div className="relative w-full max-w-md bg-white rounded-lg shadow-lg border border-gray-100">
                <div className="p-4">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Delete GRN</h3>
                      <p className="text-sm text-gray-600">This action cannot be undone.</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-6">
                    Are you sure you want to delete GRN "{showDeleteConfirm.grnNumber}"?
                  </p>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowDeleteConfirm(null)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDelete}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Status Update Modal */}
          {showStatusUpdate && (
            <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2">
              <div className="relative w-full max-w-md bg-white rounded-lg shadow-lg border border-gray-100">
                <div className="p-4">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Update GRN Status</h3>
                      <p className="text-sm text-gray-600">Change status for {showStatusUpdate.grnNumber}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">Select Status</option>
                      <option value="draft">Draft</option>
                      <option value="received">Received</option>
                      <option value="inspected">Inspected</option>
                      <option value="accepted">Accepted</option>
                      <option value="partially_accepted">Partially Accepted</option>
                      <option value="rejected">Rejected</option>
                      <option value="returned">Returned</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Comments
                    </label>
                    <textarea
                      value={statusComments}
                      onChange={(e) => setStatusComments(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      rows="3"
                      placeholder="Add comments about this status change..."
                    ></textarea>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowStatusUpdate(null);
                        setNewStatus('');
                        setStatusComments('');
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmStatusUpdate}
                      disabled={!newStatus.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Update Status
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Approve Confirmation Modal */}
          {showApproveConfirm && (
            <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2">
              <div className="relative w-full max-w-md bg-white rounded-lg shadow-lg border border-gray-100">
                <div className="p-4">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Approve GRN</h3>
                      <p className="text-sm text-gray-600">Confirm goods receipt approval</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-4">
                    Are you sure you want to approve GRN "{showApproveConfirm.grnNumber}"?
                  </p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Approval Comments <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={approvalComments}
                      onChange={(e) => setApprovalComments(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      rows="3"
                      placeholder="Add your approval comments..."
                    ></textarea>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowApproveConfirm(null);
                        setApprovalComments('');
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmApproval}
                      disabled={!approvalComments.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Approve GRN
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reject Confirmation Modal */}
          {showRejectConfirm && (
            <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2">
              <div className="relative w-full max-w-md bg-white rounded-lg shadow-lg border border-gray-100">
                <div className="p-4">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Reject GRN</h3>
                      <p className="text-sm text-gray-600">Specify rejection reason</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-4">
                    Are you sure you want to reject GRN "{showRejectConfirm.grnNumber}"?
                  </p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rejection Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={rejectionComments}
                      onChange={(e) => setRejectionComments(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      rows="3"
                      placeholder="Provide detailed reason for rejection..."
                    ></textarea>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowRejectConfirm(null);
                        setRejectionComments('');
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmRejection}
                      disabled={!rejectionComments.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Reject GRN
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bulk Actions Modal */}
          {showBulkActions && (
            <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2">
              <div className="relative w-full max-w-md bg-white rounded-lg shadow-lg border border-gray-100">
                <div className="p-4">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Bulk Action</h3>
                      <p className="text-sm text-gray-600">{selectedGRNs.size} GRN(s) selected</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Action: {bulkAction?.charAt(0).toUpperCase() + bulkAction?.slice(1)}
                    </label>
                    <p className="text-sm text-gray-600 mb-3">
                      {bulkAction === 'approve' && 'This will approve all selected GRNs.'}
                      {bulkAction === 'reject' && 'This will reject all selected GRNs.'}
                      {bulkAction === 'export' && 'This will export all selected GRNs to CSV.'}
                    </p>
                  </div>

                  {(bulkAction === 'approve' || bulkAction === 'reject') && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {bulkAction === 'approve' ? 'Comments' : 'Rejection Reason'}
                        {bulkAction === 'reject' && <span className="text-red-500">*</span>}
                      </label>
                      <textarea
                        value={bulkComments}
                        onChange={(e) => setBulkComments(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        rows="3"
                        placeholder={`Add ${bulkAction === 'approve' ? 'approval comments' : 'rejection reason'}...`}
                      ></textarea>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowBulkActions(false);
                        setBulkAction('');
                        setBulkComments('');
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={executeBulkAction}
                      disabled={loading || (bulkAction === 'reject' && !bulkComments.trim())}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? 'Processing...' : `${bulkAction?.charAt(0).toUpperCase() + bulkAction?.slice(1)} Selected`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Purchase Order Selection Modal */}
          {showPOSelection && (
            <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2">
              <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-lg border border-gray-100">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Select Purchase Order</h3>
                        <p className="text-sm text-gray-600">Choose a purchase order to create GRN</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowPOSelection(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="max-h-96 overflow-y-auto">
                    {availablePOs.length === 0 ? (
                      <div className="text-center py-8">
                        <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-gray-500">No approved purchase orders available for GRN creation</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {availablePOs.map((po) => (
                          <div
                            key={po._id}
                            onClick={() => handlePOSelect(po)}
                            className="p-4 border border-gray-200 rounded-lg hover:bg-indigo-50 hover:border-indigo-300 cursor-pointer transition-all"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3">
                                  <h4 className="text-sm font-medium text-gray-900">{po.orderNumber}</h4>
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${po.status === 'approved' ? 'bg-teal-100 text-teal-800' :
                                    po.status === 'sent_to_supplier' ? 'bg-indigo-100 text-indigo-800' :
                                      po.status === 'acknowledged' ? 'bg-purple-100 text-purple-800' :
                                        'bg-gray-100 text-gray-800'
                                    }`}>
                                    {po.status?.replace('_', ' ').toUpperCase()}
                                  </span>
                                </div>
                                <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                                  <span>Supplier: {po.supplier?.name || 'N/A'}</span>
                                  <span>Date: {formatDate(po.orderDate)}</span>
                                  <span>Value: {formatCurrency(po.totals?.totalAmount)}</span>
                                </div>
                                <div className="mt-1 text-xs text-gray-400">
                                  {po.lineItems?.length || 0} items • Expected: {formatDate(po.expectedDelivery)}
                                </div>
                              </div>
                              <div className="flex items-center">
                                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end mt-4">
                    <button
                      onClick={() => setShowPOSelection(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          {!showForm && (
            <div className="bg-white shadow-sm border border-gray-200">
              {/* Filters and Search */}
              <div className="px-4 sm:px-6 lg:px-8 py-2 border-b border-gray-200 flex-shrink-0 bg-gray-50">
                <div className="flex flex-col lg:flex-row lg:items-center gap-2">
                  {/* Search */}
                  <div className="flex-1">
                    <TextInput
                      variant="input"
                      type="text"
                      placeholder="Search by GRN number, delivery note, supplier..."
                      value={searchTerm}
                      onChange={handleSearch}
                      icon={({ className }) => (
                        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      )}
                      className="text-sm py-1.5 !bg-white border border-gray-300 shadow-sm !rounded-sm"
                    />
                  </div>

                  {/* Filters and Actions */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    {/* Status Filter */}
                    <CustomSelect
                      value={statusFilter}
                      onChange={handleStatusFilter}
                      options={statusOptions}
                      size="sm"
                      variant="default"
                      className="min-w-[140px]"
                    />

                    {/* Supplier Filter */}
                    <CustomSelect
                      value={supplierFilter}
                      onChange={handleSupplierFilter}
                      options={[
                        { value: 'all', label: 'All Suppliers' },
                        ...suppliers.map(supplier => ({
                          value: supplier._id,
                          label: supplier.name
                        }))
                      ]}
                      size="sm"
                      variant="default"
                      className="min-w-[140px]"
                    />

                    {/* Warehouse Filter */}
                    <CustomSelect
                      value={warehouseFilter}
                      onChange={handleWarehouseFilter}
                      options={[
                        { value: 'all', label: 'All Warehouses' },
                        ...warehouses.map(warehouse => ({
                          value: warehouse._id,
                          label: warehouse.name
                        }))
                      ]}
                      size="sm"
                      variant="default"
                      className="min-w-[140px]"
                    />
                    {/* View Toggle */}
                    <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5">
                      <button
                        onClick={() => handleViewChange('table')}
                        className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'table'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                          }`}
                      >
                        <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                        List
                      </button>
                      <button
                        onClick={() => handleViewChange('card')}
                        className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'card'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                          }`}
                      >
                        <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                        </svg>
                        Cards
                      </button>
                    </div>

                    {/* Refresh Button */}
                    <button
                      onClick={fetchGRNs}
                      className="px-3 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-1"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span className="hidden sm:inline">Refresh</span>
                    </button>

                    {/* Clear Filters */}
                    <button
                      onClick={clearFilters}
                      className="px-3 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Clear
                    </button>

                    {/* Add GRN Button */}
                    <PermissionGuard permission="INVENTORY:GRN:CREATE">
                      <button
                        onClick={handleCreateGRN}
                        className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200"
                      >
                        <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add GRN
                      </button>
                    </PermissionGuard>
                  </div>
                </div>
              </div>

              {/* Pagination */}
              {pagination.total > 0 && (
                <div className="flex items-center justify-between pb-2 border-b  px-2  py-2 bg-gradient-to-r from-gray-100 to-gray-100 mb-6 border-b border-gray-200">
                  {/* Results Info */}
                  <div className="flex items-center gap-2">
                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-1.5 rounded-lg shadow-sm">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {(pagination.page - 1) * pagination.limit + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} GRNs
                      </p>
                      <p className="text-xs text-gray-500">
                        {hasFilters() ? 'Filtered results' : 'All GRNs'}
                      </p>
                    </div>
                  </div>

                  {/* Navigation Controls */}
                  <div className="flex items-center gap-3">
                    {/* Items per page */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 font-medium">Show:</span>
                      <select
                        value={pagination.limit}
                        onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                        disabled={loading}
                        className="px-2 py-1 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[70px]"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>

                    {/* Navigation Buttons */}
                    {pagination.pages > 1 && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handlePageChange(1)}
                          disabled={!pagination.hasPrev || loading}
                          className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                          title="First page"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                          </svg>
                        </button>

                        <button
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={!pagination.hasPrev || loading}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                          <span className="hidden sm:inline">Previous</span>
                        </button>

                        {/* Page Numbers */}
                        <div className="hidden md:flex items-center gap-1">
                          {Array.from({ length: Math.min(7, pagination.pages) }, (_, i) => {
                            const pageNum = i + 1;
                            const isCurrentPage = pageNum === pagination.page;

                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                disabled={loading}
                                className={`min-w-[32px] px-2.5 py-1.5 rounded text-sm font-medium transition-all ${isCurrentPage
                                  ? 'bg-indigo-600 text-white shadow-sm'
                                  : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                                  }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                          {pagination.pages > 7 && (
                            <span className="px-2 text-gray-400 text-sm">...</span>
                          )}
                        </div>

                        {/* Mobile page indicator */}
                        <div className="flex md:hidden px-3 py-1.5 bg-white rounded border border-gray-200">
                          <span className="text-sm font-medium text-gray-900">{pagination.page}</span>
                          <span className="text-sm text-gray-500 mx-1">of</span>
                          <span className="text-sm font-medium text-gray-900">{pagination.pages}</span>
                        </div>

                        <button
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={!pagination.hasNext || loading}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                        >
                          <span className="hidden sm:inline">Next</span>
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>

                        <button
                          onClick={() => handlePageChange(pagination.pages)}
                          disabled={!pagination.hasNext || loading}
                          className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                          title="Last page"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Content Area */}
              <div className="p-2">
                {loading ? (
                  <LoadingSpinner />
                ) : filteredGRNs.length === 0 ? (
                  <div className="text-center py-12">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No GRNs found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {hasFilters() ?
                        'Try adjusting your search or filter criteria.' :
                        'Get started by creating your first GRN.'
                      }
                    </p>
                    {!hasFilters() && (
                      <div className="mt-6">
                        <PermissionGuard permission="INVENTORY:GRN:CREATE">
                          <button
                            onClick={handleCreateGRN}
                            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200"
                          >
                            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Create GRN
                          </button>
                        </PermissionGuard>
                      </div>
                    )}
                  </div>
                ) : (
                  <>

                    {/* Table View */}
                    {view === 'table' && (
                      <div className="overflow-x-auto">
                        <table className="min-w-max w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left">
                                <input
                                  type="checkbox"
                                  checked={selectedGRNs.size === filteredGRNs.length && filteredGRNs.length > 0}
                                  onChange={handleSelectAllGRNs}
                                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                />
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                GRN Details
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Purchase Order
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Supplier
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Inspection
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Total Value
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredGRNs.map((grn) => (
                              <tr key={grn._id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <input
                                    type="checkbox"
                                    checked={selectedGRNs.has(grn._id)}
                                    onChange={() => handleSelectGRN(grn._id)}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                  />
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div>
                                    <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                                      <DoubleEntryToggleButton
                                        open={doubleEntryGRN?._id === grn._id}
                                        onClick={() => setDoubleEntryGRN(doubleEntryGRN?._id === grn._id ? null : grn)}
                                      />
                                      {grn.grnNumber}
                                      {grn.procurementType === 'SERVICE' && <span className="ml-1 inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-sky-100 text-sky-700" title="Service completion">Service</span>}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {formatDate(grn.receiptDate)}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">
                                    {grn.purchaseOrder?.orderNumber || 'N/A'}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">
                                    {grn.supplier?.name || 'N/A'}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(grn.status)}`}>
                                    {grn.status?.replace('_', ' ').toUpperCase()}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getInspectionStatusColor(grn.qualityInspection?.status)}`}>
                                    {grn.qualityInspection?.status?.replace('_', ' ').toUpperCase() || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">
                                    {formatCurrency(grn.totals?.grandTotal)}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                  <div className="flex items-center justify-end space-x-2">
                                    <PermissionGuard permission="INVENTORY:GRN:READ">
                                      <button
                                        onClick={() => handleViewGRN(grn)}
                                        className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
                                        title="View details"
                                      >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                      </button>
                                    </PermissionGuard>

                                    {canPrintGRN(grn.status) && (
                                      <button
                                        onClick={() => handlePrintGRN(grn)}
                                        className="text-gray-600 hover:text-gray-900 p-1 hover:bg-gray-50 rounded transition-all"
                                        title="Print PDF"
                                      >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                        </svg>
                                      </button>
                                    )}

                                    {(grn.status === 'draft' || grn.status === 'pending') && (
                                      <PermissionGuard permission="INVENTORY:GRN:UPDATE">
                                        <button
                                          onClick={() => handleEditGRN(grn)}
                                          className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
                                          title="Edit GRN"
                                        >
                                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                          </svg>
                                        </button>
                                      </PermissionGuard>
                                    )}

                                    <PermissionGuard permission="INVENTORY:GRN:UPDATE">
                                      <button
                                        onClick={() => handleUpdateStatus(grn)}
                                        className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
                                        title="Update Status"
                                      >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                      </button>
                                    </PermissionGuard>

                                    {grn.qualityInspection?.required &&
                                      ['received', 'inspected'].includes(grn.status) && (
                                        <PermissionGuard permission="INVENTORY:GRN:INSPECT">
                                          <button
                                            onClick={() => handleInspectGRN(grn)}
                                            className="text-yellow-600 hover:text-yellow-900 p-1 hover:bg-yellow-50 rounded transition-all"
                                            title="Inspect"
                                          >
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                            </svg>
                                          </button>
                                        </PermissionGuard>
                                      )}

                                    {['inspected', 'accepted', 'partially_accepted'].includes(grn.status) &&
                                      !grn.approvedBy && (
                                        <>
                                          <PermissionGuard permission="INVENTORY:GRN:APPROVE">
                                            <button
                                              onClick={() => handleApproveGRN(grn)}
                                              className="text-teal-600 hover:text-teal-900 p-1 hover:bg-teal-50 rounded transition-all"
                                              title="Approve"
                                            >
                                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                              </svg>
                                            </button>
                                          </PermissionGuard>

                                          <PermissionGuard permission="INVENTORY:GRN:REJECT">
                                            <button
                                              onClick={() => handleRejectGRN(grn)}
                                              className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all"
                                              title="Reject"
                                            >
                                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                              </svg>
                                            </button>
                                          </PermissionGuard>
                                        </>
                                      )}

                                    {(grn.status === 'draft' || grn.status === 'pending') && (
                                      <PermissionGuard permission="INVENTORY:GRN:DELETE">
                                        <button
                                          onClick={() => handleDeleteGRN(grn)}
                                          className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all"
                                          title="Delete"
                                        >
                                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                          </svg>
                                        </button>
                                      </PermissionGuard>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Card View */}
                    {doubleEntryGRN && (
                      <DoubleEntryModal
                        documentType="GoodsReceiptNote"
                        documentId={doubleEntryGRN._id}
                        title={doubleEntryGRN.grnNumber}
                        onClose={() => setDoubleEntryGRN(null)}
                      />
                    )}

                    {view === 'card' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {filteredGRNs.map((grn) => (
                          <div key={grn._id} className="bg-white border border-gray-200 rounded shadow-sm hover:shadow-md transition-all duration-200 group h-40 w-full">
                            <div className="p-3 h-full flex flex-col">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center space-x-2 flex-1 min-w-0">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-xs font-semibold text-indigo-700 flex-shrink-0">
                                    {grn.grnNumber?.substring(0, 2)?.toUpperCase() || 'GR'}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <h3 className="text-sm font-semibold text-gray-900 truncate group-hover:text-indigo-600 transition-colors">
                                      {grn.grnNumber}
                                      {grn.procurementType === 'SERVICE' && <span className="ml-1 inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-sky-100 text-sky-700" title="Service completion">Service</span>}
                                    </h3>
                                    <p className="text-xs text-gray-500 truncate">{formatDate(grn.receiptDate)}</p>
                                  </div>
                                </div>

                                <div className="flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
                                  <DoubleEntryToggleButton
                                    className="mr-0.5"
                                    open={doubleEntryGRN?._id === grn._id}
                                    onClick={() => setDoubleEntryGRN(doubleEntryGRN?._id === grn._id ? null : grn)}
                                  />
                                  <PermissionGuard permission="INVENTORY:GRN:READ">
                                    <button
                                      onClick={() => handleViewGRN(grn)}
                                      className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                                      title="View GRN"
                                    >
                                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                      </svg>
                                    </button>
                                  </PermissionGuard>

                                  {(grn.status === 'draft' || grn.status === 'pending') && (
                                    <PermissionGuard permission="INVENTORY:GRN:UPDATE">
                                      <button
                                        onClick={() => handleEditGRN(grn)}
                                        className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                                        title="Edit GRN"
                                      >
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                      </button>
                                    </PermissionGuard>
                                  )}

                                  {(grn.status === 'draft' || grn.status === 'pending') && (
                                    <PermissionGuard permission="INVENTORY:GRN:DELETE">
                                      <button
                                        onClick={() => handleDeleteGRN(grn)}
                                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                        title="Delete GRN"
                                      >
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </PermissionGuard>
                                  )}
                                </div>
                              </div>

                              <div className="text-xs text-gray-600 mb-2 flex-1">
                                <div className="flex justify-between mb-1">
                                  <span>PO:</span>
                                  <span className="font-medium truncate ml-1">{grn.purchaseOrder?.orderNumber || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between mb-1">
                                  <span>Supplier:</span>
                                  <span className="font-medium truncate ml-1">{grn.supplier?.name || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Value:</span>
                                  <span className="font-medium">{formatCurrency(grn.totals?.grandTotal)}</span>
                                </div>
                              </div>

                              <div className="flex flex-wrap gap-1 mt-auto">
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getStatusColor(grn.status)}`}>
                                  {grn.status?.replace('_', ' ').toUpperCase()}
                                </span>
                                {grn.qualityInspection?.required && (
                                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getInspectionStatusColor(grn.qualityInspection?.status)}`}>
                                    QC: {grn.qualityInspection?.status?.replace('_', ' ').toUpperCase() || 'Pending'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
      </div>
    </PermissionGuard>
  );
};

export default GRNManagement;